import {
  ExtendedFields,
  parseFqdn,
  CustomFieldType,
  CustomFieldsViewWidgetState,
} from '@wix/bex-core';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Cell,
  Divider,
  EmptyState,
  Heading,
  Layout,
  Text,
} from '@wix/design-system';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { SkeletonContent } from './Skeletons';
import { ErrorStateContent } from './ErrorStateContent';
import { TextProps } from '@wix/design-system/dist/types/Text';
import { classes, st } from './CustomFieldsViewWidget.st.css.js';

export interface CustomFieldsViewWidgetProps {
  /**
   * Applies a data-hook HTML attribute that can be used in tests.
   */
  dataHook?: string;

  /**
   * The FQDN to be used to extract schema from data-extension service.
   */
  fqdn: string;

  /**
   * Existing extended fields values, usually under `myEntity.extendedFields`.
   */
  extendedFields?: ExtendedFields;

  /**
   * Overrides the default widget title.
   */
  title?: string;
}

const _CustomFieldsViewWidget = ({
  dataHook,
  fqdn,
  extendedFields,
  title,
}: CustomFieldsViewWidgetProps) => {
  const container = useWixPatternsContainer();
  const { translate: t, dateFormatters } = container;

  const [state] = useState(
    () =>
      new CustomFieldsViewWidgetState({
        container,
        fqdn: parseFqdn(fqdn),
        url: window.location.href,
      }),
  );

  const customFields = state.dataExtension.availableUserCustomFields;

  useEffect(() => state.init(), []);

  const headerTitle = (
    <Box gap="SP1">
      <Heading appearance="H3">
        {title ?? t('cairo.customFields.entityPage.title')}
      </Heading>
    </Box>
  );

  const valueWrapper = (value: any, props: TextProps) => {
    if (typeof value === 'string' && !/\s/.test(value)) {
      // if there are no space symbols we apply ellipsis (e.g. for links, emails, etc.)
      return (
        <Text {...props} ellipsis maxLines={1}>
          {value}
        </Text>
      );
    }
    return (
      <Text {...props} className={st(classes.breakWord)}>
        {value}
      </Text>
    );
  };

  const areAllCustomFieldsEmpty = useMemo(
    () =>
      customFields.every((field) => {
        const value = extendedFields?.namespaces?._user_fields?.[field.id];
        return value === undefined || value === null;
      }),
    [customFields, extendedFields],
  );

  const getViewValue = useCallback(
    (value: any, type: CustomFieldType, valueDataHook: string) => {
      if (value === undefined || value === null) {
        return null;
      }

      const textProps: TextProps = {
        dataHook: valueDataHook,
        size: 'small',
        secondary: true,
      };
      let newValue = value;

      switch (type) {
        case 'dropdown':
          newValue = Array.isArray(value) ? value.join(', ') : value;
          break;
        case 'date':
          newValue = dateFormatters.medium.format(new Date(value));
          break;
        case 'dateTime':
          newValue = dateFormatters.mediumTime.format(new Date(value));
          break;
        case 'checkbox':
          newValue = value
            ? t('cairo.customFields.booleanValue.true')
            : t('cairo.customFields.booleanValue.false');
          break;
        default:
          break;
      }
      return valueWrapper(newValue, textProps);
    },
    [],
  );

  const getNonEmptyValues = () =>
    customFields
      .map((field) => ({
        value: getViewValue(
          extendedFields?.namespaces?._user_fields?.[field.id],
          field.type,
          'custom-field-value',
        ),
        label: field.label,
      }))
      .filter((field) => field.value !== null);

  let child: JSX.Element;

  switch (true) {
    case state.isLoading:
      child = <SkeletonContent />;
      break;
    case state.isError:
      child = <ErrorStateContent state={state} />;
      break;
    case areAllCustomFieldsEmpty:
      child = (
        <EmptyState
          dataHook="empty-state"
          title={t('cairo.customFields.entityPage.emptyState.title')}
          subtitle={
            <Text dataHook="empty-state-subtitle" size="small" secondary widows>
              {t('cairo.customFields.entityPage.emptyState.subtitle')}
            </Text>
          }
        />
      );
      break;
    default:
      child = (
        <Layout gap="SP3" dataHook="fields-content">
          {getNonEmptyValues().map((field, i, arr) => (
            <Cell>
              <Box
                marginBottom={i === arr.length - 1 ? undefined : 'SP3'}
                direction="vertical"
                gap="SP1"
              >
                <Text
                  weight="normal"
                  size="small"
                  ellipsis
                  dataHook="custom-field-label"
                >
                  {field.label}:
                </Text>
                {field.value}
              </Box>
              {i === arr.length - 1 ? undefined : (
                <Divider direction="horizontal" />
              )}
            </Cell>
          ))}
        </Layout>
      );
  }

  return customFields.length || state.isLoading || state.isError ? (
    <Card dataHook={dataHook} stretchVertically>
      <Card.Header
        dataHook="custom-fields-view-widget-header"
        title={headerTitle}
      />
      <Card.Divider />
      <Card.Content dataHook="custom-fields-view-widget-content">
        {child}
      </Card.Content>
    </Card>
  ) : null;
};

export const CustomFieldsViewWidget = observer(_CustomFieldsViewWidget);
